import { useState, useEffect } from 'react'
import {
  Box,
  Button,
  Flex,
  Input,
  Text,
  Textarea,
  Grid,
  GridItem,
  DialogRoot,
  DialogBackdrop,
  DialogPositioner,
  DialogContent,
  DialogCloseTrigger,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  Badge,
  HStack,
  Tabs,
} from '@chakra-ui/react'
import { NativeSelectRoot, NativeSelectField } from '@chakra-ui/react'
import { FileText, Library, Plus, ChevronDown } from 'lucide-react'
import { Field } from '@/components/ui/field'
import {
  useCreateResolution,
  useUpdateResolution,
  useResolutionTemplates,
} from '../hooks/useResolutions'
import { useDistributionKeys } from '@/features/accounting/hooks/useDistributionKeys'
import type {
  Resolution,
  ResolutionInsert,
  ResolutionUpdate,
  ResolutionCategory,
  MajorityType,
  ResolutionTemplate,
} from '../types'
import {
  RESOLUTION_CATEGORY_LABELS,
  MAJORITY_TYPE_LABELS,
  MAJORITY_TYPE_DESCRIPTIONS,
} from '../types'
import { meetingsToaster as toaster } from '../pages/MeetingsLayout'

export interface ResolutionFormModalProps {
  isOpen: boolean
  onClose: () => void
  meetingId: string
  residenceId: string
  resolution?: Resolution // Si fourni, mode édition
}

// Mapping catégorie -> majorité par défaut selon les specs
const CATEGORY_DEFAULT_MAJORITY: Record<ResolutionCategory, MajorityType> = {
  approval_accounts: 'simple',
  approval_budget: 'simple',
  works_maintenance: 'simple',
  works_improvement: 'absolute',
  works_major: 'double',
  contract_renewal: 'simple',
  contract_new: 'simple',
  syndic_mandate: 'absolute',
  council_election: 'absolute',
  council_powers: 'absolute',
  rules_modification: 'double',
  common_parts: 'double',
  miscellaneous: 'simple',
}

// Catégories qui nécessitent un montant
const CATEGORIES_WITH_AMOUNT: ResolutionCategory[] = [
  'works_maintenance',
  'works_improvement',
  'works_major',
  'approval_budget',
]

interface FormData {
  title: string
  description: string
  resolution_category: ResolutionCategory
  majority_type: MajorityType
  distribution_key_id: string
  amount: string
  notes: string
}

const initialFormData: FormData = {
  title: '',
  description: '',
  resolution_category: 'miscellaneous',
  majority_type: 'simple',
  distribution_key_id: '',
  amount: '',
  notes: '',
}

export function ResolutionFormModal({
  isOpen,
  onClose,
  meetingId,
  residenceId,
  resolution,
}: ResolutionFormModalProps) {
  const [formData, setFormData] = useState<FormData>(initialFormData)
  const [activeTab, setActiveTab] = useState<'form' | 'templates'>('form')

  const { data: templates = [] } = useResolutionTemplates()
  const { data: distributionKeys = [] } = useDistributionKeys(residenceId)
  const createResolution = useCreateResolution()
  const updateResolution = useUpdateResolution()

  const isEditMode = !!resolution

  // Initialiser le formulaire en mode édition
  useEffect(() => {
    if (resolution) {
      setFormData({
        title: resolution.title,
        description: resolution.description || '',
        resolution_category: resolution.resolution_category || 'miscellaneous',
        majority_type: resolution.majority_type || 'simple',
        distribution_key_id: resolution.distribution_key_id || '',
        amount: resolution.amount?.toString() || '',
        notes: resolution.notes || '',
      })
    } else {
      setFormData(initialFormData)
    }
  }, [resolution, isOpen])

  // Sélectionner la clé de répartition par défaut (tantièmes généraux)
  useEffect(() => {
    if (!formData.distribution_key_id && distributionKeys.length > 0) {
      const generalKey = distributionKeys.find(
        (k) => k.name?.toLowerCase().includes('généra') || k.name?.toLowerCase().includes('general')
      )
      if (generalKey) {
        setFormData((prev) => ({ ...prev, distribution_key_id: generalKey.id }))
      } else {
        // Fallback sur la première clé si pas de "générale"
        const firstKey = distributionKeys[0]
        if (firstKey) {
          setFormData((prev) => ({ ...prev, distribution_key_id: firstKey.id }))
        }
      }
    }
  }, [distributionKeys, formData.distribution_key_id])

  const handleClose = () => {
    setFormData(initialFormData)
    setActiveTab('form')
    onClose()
  }

  const handleChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }))

    // Mettre à jour la majorité par défaut si on change la catégorie
    if (field === 'resolution_category') {
      const category = value as ResolutionCategory
      const defaultMajority = CATEGORY_DEFAULT_MAJORITY[category]
      setFormData((prev) => ({ ...prev, majority_type: defaultMajority }))
    }
  }

  const handleSelectTemplate = (template: ResolutionTemplate) => {
    setFormData({
      title: template.title,
      description: template.description_template || '',
      resolution_category: template.resolution_category || 'miscellaneous',
      majority_type: template.majority_type || 'simple',
      distribution_key_id: formData.distribution_key_id, // Garder la clé existante
      amount: '',
      notes: '',
    })
    setActiveTab('form')
  }

  const handleSubmit = async () => {
    if (!formData.title) {
      toaster.create({
        title: 'Champ requis',
        description: 'Veuillez saisir un titre pour la résolution',
        type: 'error',
      })
      return
    }

    try {
      if (isEditMode && resolution) {
        const updateData: ResolutionUpdate = {
          title: formData.title,
          description: formData.description || null,
          resolution_category: formData.resolution_category,
          majority_type: formData.majority_type,
          distribution_key_id: formData.distribution_key_id || null,
          amount: formData.amount ? parseFloat(formData.amount) : null,
          notes: formData.notes || null,
        }

        await updateResolution.mutateAsync({
          id: resolution.id,
          data: updateData,
        })

        toaster.create({
          title: 'Résolution modifiée',
          description: 'La résolution a été mise à jour',
          type: 'success',
        })
      } else {
        const insertData: ResolutionInsert = {
          meeting_id: meetingId,
          title: formData.title,
          description: formData.description || null,
          resolution_category: formData.resolution_category,
          majority_type: formData.majority_type,
          distribution_key_id: formData.distribution_key_id || null,
          amount: formData.amount ? parseFloat(formData.amount) : null,
          notes: formData.notes || null,
          status: 'DRAFT',
        }

        await createResolution.mutateAsync(insertData)

        toaster.create({
          title: 'Résolution ajoutée',
          description: 'La résolution a été ajoutée à l\'ordre du jour',
          type: 'success',
        })
      }

      handleClose()
    } catch (error) {
      console.error('Error saving resolution:', error)
      toaster.create({
        title: 'Erreur',
        description: 'Impossible de sauvegarder la résolution',
        type: 'error',
      })
    }
  }

  const showAmount = CATEGORIES_WITH_AMOUNT.includes(formData.resolution_category)

  // Grouper les templates par catégorie
  const templatesByCategory = templates.reduce(
    (acc, template) => {
      const cat = template.resolution_category || 'miscellaneous'
      if (!acc[cat]) acc[cat] = []
      acc[cat].push(template)
      return acc
    },
    {} as Record<ResolutionCategory, ResolutionTemplate[]>
  )

  return (
    <DialogRoot
      open={isOpen}
      onOpenChange={(e) => !e.open && handleClose()}
      size="xl"
    >
      <DialogBackdrop />
      <DialogPositioner>
        <DialogContent maxW="800px">
          <DialogCloseTrigger />
          <DialogHeader>
            <DialogTitle>
              <Flex align="center" gap={2}>
                <FileText size={20} />
                {isEditMode ? 'Modifier la résolution' : 'Ajouter une résolution'}
              </Flex>
            </DialogTitle>
          </DialogHeader>

          <DialogBody>
            {!isEditMode && (
              <Tabs.Root
                value={activeTab}
                onValueChange={(e) => setActiveTab(e.value as 'form' | 'templates')}
                variant="line"
                mb={4}
              >
                <Tabs.List>
                  <Tabs.Trigger value="form">
                    <Plus size={14} />
                    Nouvelle résolution
                  </Tabs.Trigger>
                  <Tabs.Trigger value="templates">
                    <Library size={14} />
                    Depuis un modèle
                  </Tabs.Trigger>
                </Tabs.List>
              </Tabs.Root>
            )}

            {activeTab === 'templates' && !isEditMode ? (
              <Box maxH="400px" overflowY="auto">
                {Object.entries(templatesByCategory).map(([category, catTemplates]) => (
                  <Box key={category} mb={4}>
                    <Text
                      fontSize="sm"
                      fontWeight="semibold"
                      color="gray.600"
                      mb={2}
                      textTransform="uppercase"
                    >
                      {RESOLUTION_CATEGORY_LABELS[category as ResolutionCategory]}
                    </Text>
                    <Flex direction="column" gap={2}>
                      {catTemplates.map((template) => (
                        <Box
                          key={template.id}
                          p={3}
                          borderWidth="1px"
                          borderRadius="md"
                          cursor="pointer"
                          _hover={{ bg: 'gray.50', borderColor: 'brand.500' }}
                          onClick={() => handleSelectTemplate(template)}
                        >
                          <HStack justify="space-between" mb={1}>
                            <Text fontWeight="medium">{template.title}</Text>
                            <Badge
                              colorPalette="purple"
                              size="sm"
                            >
                              {MAJORITY_TYPE_LABELS[template.majority_type as MajorityType]}
                            </Badge>
                          </HStack>
                          {template.legal_reference && (
                            <Text fontSize="xs" color="gray.500">
                              {template.legal_reference}
                            </Text>
                          )}
                        </Box>
                      ))}
                    </Flex>
                  </Box>
                ))}
                {templates.length === 0 && (
                  <Text color="gray.500" textAlign="center" py={8}>
                    Aucun modèle disponible
                  </Text>
                )}
              </Box>
            ) : (
              <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                {/* Titre */}
                <GridItem colSpan={2}>
                  <Field label="Titre de la résolution" required>
                    <Input
                      placeholder="Ex: Approbation des comptes de l'exercice 2024"
                      value={formData.title}
                      onChange={(e) => handleChange('title', e.target.value)}
                    />
                  </Field>
                </GridItem>

                {/* Catégorie */}
                <GridItem colSpan={1}>
                  <Field label="Catégorie" required>
                    <Box position="relative">
                      <NativeSelectRoot>
                        <NativeSelectField
                          value={formData.resolution_category}
                          onChange={(e) =>
                            handleChange('resolution_category', e.target.value)
                          }
                        >
                          {Object.entries(RESOLUTION_CATEGORY_LABELS).map(
                            ([value, label]) => (
                              <option key={value} value={value}>
                                {label}
                              </option>
                            )
                          )}
                        </NativeSelectField>
                      </NativeSelectRoot>
                      <Box
                        position="absolute"
                        right="3"
                        top="50%"
                        transform="translateY(-50%)"
                        pointerEvents="none"
                        color="gray.500"
                      >
                        <ChevronDown size={16} />
                      </Box>
                    </Box>
                  </Field>
                </GridItem>

                {/* Majorité */}
                <GridItem colSpan={1}>
                  <Field label="Majorité requise" required>
                    <Box position="relative">
                      <NativeSelectRoot>
                        <NativeSelectField
                          value={formData.majority_type}
                          onChange={(e) => handleChange('majority_type', e.target.value)}
                        >
                          {Object.entries(MAJORITY_TYPE_LABELS).map(
                            ([value, label]) => (
                              <option key={value} value={value}>
                                {label}
                              </option>
                            )
                          )}
                        </NativeSelectField>
                      </NativeSelectRoot>
                      <Box
                        position="absolute"
                        right="3"
                        top="50%"
                        transform="translateY(-50%)"
                        pointerEvents="none"
                        color="gray.500"
                      >
                        <ChevronDown size={16} />
                      </Box>
                    </Box>
                  </Field>
                </GridItem>

                {/* Info majorité */}
                <GridItem colSpan={2}>
                  <Box p={2} bg="purple.50" borderRadius="md">
                    <Text fontSize="xs" color="purple.700">
                      {MAJORITY_TYPE_DESCRIPTIONS[formData.majority_type]}
                    </Text>
                  </Box>
                </GridItem>

                {/* Clé de répartition */}
                <GridItem colSpan={1}>
                  <Field label="Clé de répartition">
                    <Box position="relative">
                      <NativeSelectRoot>
                        <NativeSelectField
                          value={formData.distribution_key_id}
                          onChange={(e) =>
                            handleChange('distribution_key_id', e.target.value)
                          }
                        >
                          <option value="">Tantièmes généraux</option>
                          {distributionKeys.map((key) => (
                            <option key={key.id} value={key.id}>
                              {key.name}
                            </option>
                          ))}
                        </NativeSelectField>
                      </NativeSelectRoot>
                      <Box
                        position="absolute"
                        right="3"
                        top="50%"
                        transform="translateY(-50%)"
                        pointerEvents="none"
                        color="gray.500"
                      >
                        <ChevronDown size={16} />
                      </Box>
                    </Box>
                  </Field>
                </GridItem>

                {/* Montant (conditionnel) */}
                {showAmount && (
                  <GridItem colSpan={1}>
                    <Field label="Montant (€)">
                      <Input
                        type="number"
                        placeholder="0.00"
                        value={formData.amount}
                        onChange={(e) => handleChange('amount', e.target.value)}
                        min={0}
                        step="0.01"
                      />
                    </Field>
                  </GridItem>
                )}

                {/* Description */}
                <GridItem colSpan={2}>
                  <Field label="Description / Exposé des motifs">
                    <Textarea
                      placeholder="Détaillez le contenu de la résolution..."
                      value={formData.description}
                      onChange={(e) => handleChange('description', e.target.value)}
                      rows={4}
                    />
                  </Field>
                </GridItem>

                {/* Notes */}
                <GridItem colSpan={2}>
                  <Field label="Notes internes">
                    <Textarea
                      placeholder="Notes pour usage interne (ne seront pas sur la convocation)..."
                      value={formData.notes}
                      onChange={(e) => handleChange('notes', e.target.value)}
                      rows={2}
                    />
                  </Field>
                </GridItem>
              </Grid>
            )}
          </DialogBody>

          <DialogFooter>
            <Flex gap={2}>
              <Button variant="ghost" onClick={handleClose}>
                Annuler
              </Button>
              {activeTab === 'form' && (
                <Button
                  colorPalette="brand"
                  onClick={handleSubmit}
                  loading={createResolution.isPending || updateResolution.isPending}
                >
                  {isEditMode ? 'Enregistrer' : 'Ajouter'}
                </Button>
              )}
            </Flex>
          </DialogFooter>
        </DialogContent>
      </DialogPositioner>
    </DialogRoot>
  )
}
